/**
 * Phase 120 (Plan 120-01) — TanStack Query wrapper for `GET /api/release-flags`.
 *
 * Unlike `useEnabledModules` (`staleTime: Infinity`), flag state changes at
 * runtime — an admin switches a flag off, or a ticket is verified — so the
 * query goes stale after ~60 s and refetches on window focus (D-120-08).
 *
 * Plan 120-02 adds the admin page hooks. Both mutations invalidate the admin
 * list AND the delivery query, so the SPA's own flag state follows the change.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchReleaseFlags,
  fetchAdminReleaseFlags,
  switchReleaseFlagOff,
  resetReleaseFlagOverride,
  switchReleaseFlagTicketOn,
} from '../services/api/releaseFlags';
import type {
  AdminReleaseFlagRow,
  ReleaseFlagStateResult,
  ReleaseFlagSwitchOnResult,
} from '../services/api/releaseFlags';

export const RELEASE_FLAGS_QUERY_KEY = ['release-flags'] as const;
export const ADMIN_RELEASE_FLAGS_QUERY_KEY = ['admin-release-flags'] as const;

export function useReleaseFlagValues(enabled: boolean) {
  return useQuery({
    queryKey: RELEASE_FLAGS_QUERY_KEY,
    queryFn: fetchReleaseFlags,
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

/** Admin list of every registry flag (state, source, age, ticket links). */
export function useAdminReleaseFlags() {
  return useQuery<AdminReleaseFlagRow[]>({
    queryKey: ADMIN_RELEASE_FLAGS_QUERY_KEY,
    queryFn: () => fetchAdminReleaseFlags(),
    refetchOnWindowFocus: true,
  });
}

function useInvalidateFlagQueries() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ADMIN_RELEASE_FLAGS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: RELEASE_FLAGS_QUERY_KEY });
  };
}

export function useSwitchReleaseFlagOff() {
  const invalidate = useInvalidateFlagQueries();
  return useMutation<ReleaseFlagStateResult, Error, string>({
    mutationFn: (key: string) => switchReleaseFlagOff(key),
    onSuccess: invalidate,
  });
}

export function useResetReleaseFlagOverride() {
  const invalidate = useInvalidateFlagQueries();
  return useMutation<ReleaseFlagStateResult, Error, string>({
    mutationFn: (key: string) => resetReleaseFlagOverride(key),
    onSuccess: invalidate,
  });
}

/** "Switch on (mark verified)" for a ticket (Plan 120-03). */
export function useSwitchReleaseFlagTicketOn() {
  const invalidate = useInvalidateFlagQueries();
  return useMutation<ReleaseFlagSwitchOnResult, Error, string>({
    mutationFn: (ticket: string) => switchReleaseFlagTicketOn(ticket),
    onSuccess: invalidate,
  });
}
